import {
  Button,
  Divider,
  Input,
  Select,
  Table,
  TableProps,
} from "antd";
import Title from "antd/es/typography/Title";
import { useEffect, useState } from "react";
import { ProductInterface } from "../../../../interfaces/product";
import { useSelector } from "react-redux";
import { AuthenticationState } from "../../../../interfaces/users";
import { useQuery } from "react-query";
import { api } from "../../../../config";
import { SeniorityInterface } from "../../../../interfaces/seniority";
import { onChangeCurrencyInput } from "../../../Kanban/SingleTicketModal";
import { ModulePermission, userHasPermission } from "../../../../utils/helpers/userHelpers";

type EditableProduct = Omit<ProductInterface, "value"> & {
  fieldReportProductId?: number;
  value: number | string;
};

interface EditProductProps {
  ticketId: number;
  products: ProductInterface[];
  onCancel: () => void;
  onSave: () => void;
}

type ColumnTypes = Exclude<TableProps<EditableProduct>["columns"], undefined>;

const cloneProducts = (products: ProductInterface[]): EditableProduct[] =>
  products.map((product) => ({ ...product }));

const EditProduct = ({
  ticketId,
  products,
  onCancel,
  onSave,
}: EditProductProps) => {
  const { user, token } = useSelector((state: AuthenticationState) => state);
  const {
    data: seniorities,
  } = useQuery<SeniorityInterface[]>("seniorities", () =>
    api(token).get(`/seniority`)
  );
  const [dataSource, setDataSource] = useState<EditableProduct[]>(() =>
    cloneProducts(products)
  );

  useEffect(() => {
    setDataSource(cloneProducts(products));
  }, [products]);

  const defaultColumns: (ColumnTypes[number] & {
    editable?: boolean;
    inputType?: string;
    options?: { value: number; label: string }[];
    dataIndex: string;
  })[] = [
    {
      title: "Descrição",
      dataIndex: "description",
    },
    {
      title: "Quantidade",
      dataIndex: "quantity",
      render: (quantity) => `${quantity} UN`,
    },
    {
      title: "Valor Unitário",
      dataIndex: "value",
      render: (value, record) => {
        const index = dataSource.findIndex((item) => item.id === record.id);
        const item = dataSource[index];

        return (
          <Input
            style={{ width: 120 }}
            type="text"
            value={
              typeof item.value === "number"
                ? onChangeCurrencyInput(item.value.toFixed(2), undefined, 6)
                : onChangeCurrencyInput(item.value, undefined, 6)
            }
            min={1}
            disabled={
              !userHasPermission(user, {
                management: ModulePermission.ADMIN,
              })
            }
            onChange={(value) => {
              setDataSource((currentDataSource) =>
                currentDataSource.map((product) =>
                  product.id === record.id
                    ? {
                        ...product,
                        value: onChangeCurrencyInput(
                          value.target.value,
                          undefined,
                          6
                        ),
                      }
                    : product
                )
              );
            }}
          />
        );
      },
    },
    {
      title: "Código",
      dataIndex: "externalId",
      editable: true,
      render: (externalId, record) => {
        if (record.type === "COMPOSED_PRODUCT") {
          return (
            <Input
              style={{ width: 120 }}
              defaultValue={externalId}
              type="number"
              min={1}
              onChange={(externalId) => {
                setDataSource((currentDataSource) =>
                  currentDataSource.map((product) =>
                    product.id === record.id
                      ? {
                          ...product,
                          externalId: Number(externalId.target.value),
                        }
                      : product
                  )
                );
              }}
            />
          );
        }

        return externalId;
      },
    },
    {
      title: "Senioridade",
      dataIndex: "seniorityId",
      editable: true,
      render: (seniorityId, record) => {
        return (
          <Select
            defaultValue={seniorityId}
            onChange={(seniorityId) => {
              setDataSource((currentDataSource) =>
                currentDataSource.map((product) =>
                  product.id === record.id
                    ? {
                        ...product,
                        seniorityId,
                      }
                    : product
                )
              );
            }}
            style={{ width: 120 }}
            options={seniorities?.map((seniority) => {
              return {
                value: seniority.id,
                label: seniority.name,
              };
            })}
          />
        );
      },
    },
    {
      title: "Tempo de Montagem",
      dataIndex: "assemblyTime",
      editable: true,
      render: (assemblyTime, record) => {
        return (
          <Input
            style={{ width: 120 }}
            defaultValue={assemblyTime}
            type="number"
            suffix="Horas"
            max={128}
            min={1}
            onChange={(assemblyTime) => {
              setDataSource((currentDataSource) =>
                currentDataSource.map((product) =>
                  product.id === record.id
                    ? {
                        ...product,
                        assemblyTime: Number(assemblyTime.target.value),
                      }
                    : product
                )
              );
            }}
          />
        );
      },
    },
  ];

  const saveProducts = () => {
    const products = dataSource.map((product) => {
      return {
        id: product.id,
        seniorityId: product.seniorityId,
        assemblyTime: product.assemblyTime,
        externalId: product.externalId,
        value: Number(String(product.value).replace(/,/ig, '.')),
        fieldReportProductId: product.fieldReportProductId,
        // description: product.description,
        // type: product.type,
      };
    });

    api(token)
      .put(`/product`, products)
      .then((res) => {
        onSave();
      })
      .catch((err) => {
        console.log(err);
      });
  };

  return (
    <>
      <Title level={4}>Editar Produtos</Title>

      <Divider />
      <Table
        // components={components}
        rowClassName={() => "editable-row"}
        bordered
        dataSource={dataSource}
        columns={defaultColumns}
        pagination={false}
      />

      <div className="flex justify-end gap-2 my-3">
        <Button type="default" onClick={onCancel}>
          Cancelar
        </Button>
        <Button type="primary" onClick={saveProducts}>
          Salvar
        </Button>
      </div>
    </>
  );
};

export default EditProduct;
